import { faBarsFilter } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    ButtonProps,
    Checkbox,
    ListItemText,
    MenuItemProps,
    Button as MuiButton,
    MenuItem as MuiMenuItem,
    styled,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import { groupCategoriesByParentId } from "src/utils/category";

import { Type_Props_Mobile_View_Drawer } from "./MobileViewDrawer";

const MenuItem = styled(MuiMenuItem)<MenuItemProps>(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(2),
}));

const DateMenuItem = styled(MuiMenuItem)<MenuItemProps>(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    "&.Mui-focusVisible": { backgroundColor: theme.palette.transparent.main },
}));

const ResetButton = styled(MuiButton)<ButtonProps>(({ theme }) => ({
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    "&:hover": {
        color: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
    },
}));

export type Type_Filters = {
    selectedCategories: number[];
    startDate: Dayjs | null;
    endDate: Dayjs | null;
    search?: string | null;
};

const default_filter_value: Type_Filters = {
    selectedCategories: [],
    startDate: dayjs(),
    endDate: null,
};

const MobileFilterElements = ({
    closeDrawer,
}: Type_Props_Mobile_View_Drawer) => {
    const { t } = useTranslation();
    const { categories, filters, updateFilters } = useContext(MapContext); // Use filters from context
    const { language } = useContext(LanguageContext);

    const groupedCategories = groupCategoriesByParentId(categories);

    const handleCategoryChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const value = parseInt(event.target.value);
        updateFilters({
            ...filters,
            selectedCategories: filters.selectedCategories.includes(value)
                ? filters.selectedCategories.filter((id) => id !== value)
                : [...filters.selectedCategories, value],
        });
    };

    const handleDateChange = (
        name: keyof Type_Filters,
        value: Dayjs | null,
    ) => {
        updateFilters({ ...filters, [name]: value });
    };

    const handleReset = () => {
        updateFilters(default_filter_value);
        closeDrawer();
    };

    const handleSubmit = () => {
        closeDrawer();
    };

    return (
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                    component="span"
                    display="flex"
                    gap={2}
                    alignItems="center"
                >
                    <FontAwesomeIcon icon={faBarsFilter} />
                    <Typography>{t("filter")}</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {groupedCategories.map((category) => (
                    <div key={category.id}>
                        <MuiMenuItem disabled>
                            <strong>
                                {category.title[language as string]}
                            </strong>
                        </MuiMenuItem>
                        {category?.children &&
                            category.children.map((item) => (
                                <MuiMenuItem
                                    disableRipple
                                    key={item.id}
                                    onClick={() =>
                                        handleCategoryChange({
                                            target: {
                                                value: item.id.toString(),
                                            },
                                        } as unknown as React.ChangeEvent<HTMLInputElement>)
                                    }
                                >
                                    <ListItemText
                                        primary={item.title[language]}
                                    />
                                    <Checkbox
                                        checked={filters.selectedCategories.includes(
                                            item.id,
                                        )}
                                    />
                                </MuiMenuItem>
                            ))}
                    </div>
                ))}
                <MuiMenuItem disabled>
                    <strong>{t("period")}</strong>
                </MuiMenuItem>
                <DateMenuItem disableRipple>
                    <Typography color="secondary.main" variant="subtitle2">
                        {t("addElement.startDate")}
                    </Typography>
                    <DatePicker
                        value={filters.startDate}
                        onChange={(value) =>
                            handleDateChange("startDate", value)
                        }
                    />
                </DateMenuItem>
                <DateMenuItem disableRipple>
                    <Typography color="secondary.main" variant="subtitle2">
                        {t("addElement.endDate")}
                    </Typography>
                    <DatePicker
                        value={filters.endDate}
                        onChange={(value) => handleDateChange("endDate", value)}
                    />
                </DateMenuItem>

                <MenuItem disableRipple>
                    <ResetButton
                        disableRipple
                        variant="outlined"
                        onClick={handleReset}
                    >
                        {t("reset")}
                    </ResetButton>
                    <MuiButton
                        disableRipple
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        {t("validate")}
                    </MuiButton>
                </MenuItem>
            </AccordionDetails>
        </Accordion>
    );
};

export default MobileFilterElements;
